// The replan surface — product law 3 made visible (ADR-0012, ADR-0034).
//
// **This is not a list of things you failed to do.** No such list exists in this
// app. A hard date that went by becomes a live decision with its context already
// assembled, because by the time a date is behind you the real question is
// "should I still do this, and by when?" — and a bucket answers neither half.
//
// Two shapes, deliberately:
//  - The SURFACE is three quiet rows. Five options on each would be fifteen
//    buttons on one screen, which is the pile in a new costume, and this audience
//    is exactly the one for whom that is expensive.
//  - The SHEET is one item, its context, and the five options at full size with
//    their consequences spelled out. A native <dialog>, so the platform gives us
//    the modal semantics, Esc and the focus trap rather than us reimplementing
//    them badly — the same choice the detail sheet made.
//
// The options are built from REPLAN_CHOICES rather than written into the markup,
// so the surface cannot offer something the intent layer does not implement, and
// cannot drift from ADR-0012's order.

import type { Session } from './session.ts';
import type { ReplanCard } from '../replan.ts';
import { replanAll, replanWords, contextWords, REPLAN_CAP } from '../replan.ts';
import { localDayKey, atMidnight} from '../time.ts';
import { demandClocksOf } from './triage-intents.ts';
import { replanEvents, canResolve, REPLAN_CHOICES, resolveAllPassedEvents, passedDateCount } from './replan-intents.ts';
import type { ReplanChoice } from '../events.ts';
import { boundaryOf } from '../day.ts';
import { sayLasting } from './announce.ts';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
};

/**
 * How many there are, in words. Says the true total whenever the cap is hiding
 * some, because a surface that shows three of nine without saying so is lying by
 * omission — and it is a number, never a score: nothing here counts what you did
 * not do, it counts decisions waiting to be made.
 */
export function countWords(total: number, shown: number): string {
  if (total === 1) return 'One date has gone by.';
  if (total <= shown) return `${total} dates have gone by.`;
  return `${total} dates have gone by. These ${shown} first.`;
}

/**
 * The bulk gesture's two ways out, in the order that suits a SHORT lapse (3.9.0).
 *
 * `undate` leads because that is the honest answer to a day that got away: the
 * commitment has not changed, only the date is wrong. `to-menu` is the amnesty's
 * choice and is right after a fortnight, when a date that went by really has
 * stopped being a commitment — so it stays, second.
 *
 * Both say what they will do to how many things, because a control acting on
 * twenty items at once and naming none of them has to be exact about the number
 * or it is asking for trust it has not earned.
 */
export const BULK_CHOICES: { choice: ReplanChoice; label: (n: number) => string; hint: string }[] = [
  {
    choice: 'undate',
    label: n => `Take the dates off all ${n}`,
    hint: 'still yours, no dates — they come back on their own',
  },
  {
    choice: 'to-menu',
    label: n => `Put all ${n} on the Menu`,
    hint: 'nothing owed, and no date to meet',
  },
];

/** The line above them. States what the gesture acts on, and says the choice is
 *  there to be declined — the per-card options are untouched below it. */
export const bulkWords = (total: number): string =>
  `All ${total} at once, if that is easier — or take them one at a time below.`;

export interface ReplanUI { refresh(): void }

export function mountReplan(session: Session, now: () => number, onChange: () => void): ReplanUI {
  const q = <T extends HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
  const region = q('#replan');
  const heading = q('#replan-heading');
  const count = q('#replan-count');
  const list = q('#replan-cards');
  const live = q('#replan-live');
  const dlg = q<HTMLDialogElement>('#replan-sheet');
  const title = q('#replan-sheet-title');
  const when = q('#replan-sheet-when');
  const context = q('#replan-sheet-context');
  const error = q('#replan-sheet-error');
  const options = q('#replan-options');
  const bulk = q('#replan-bulk');
  const sheetLive = q('#replan-sheet-live');
  if (!region || !heading || !count || !list || !live || !dlg || !title ||
      !when || !context || !error || !options || !sheetLive) {
    return { refresh() {} };
  }
  const REGION = region, HEADING = heading, COUNT = count, LIST = list, LIVE = live,
    DLG = dlg, TITLE = title, WHEN = when, CONTEXT = context, ERROR = error,
    OPTIONS = options, SHEET_LIVE = sheetLive;
  // Optional on purpose: every other handle above is required and returns a dead
  // UI when missing, but this block is one surface's convenience and the cards
  // must still work without it.
  const BULK = bulk;

  /** What actually happened, in words, after each choice — announced rather than
   *  left for the user to infer from a row disappearing. */
  const OUTCOME: Record<ReplanChoice, string> = {
    compress: 'back today, smaller.',
    escalate: 'now a waiting-for, checked in three days.',
    renegotiate: 'the conversation comes back tomorrow.',
    'new-date': 'given a new date.',
    undate: 'back in the ordinary run of things, with no date to meet.',
    // Not "no clock" — the gate covers the cleared date with a review cure, so
    // the item does still carry one. Nothing is owed, which is the true part and
    // the part that matters.
    'to-menu': 'on the Menu — nothing owed.',
  };

  let current: ReplanCard | null = null;
  // One write at a time. Every other surface in this app learned the same lesson:
  // without it a double-tap writes the decision twice, and the log records an
  // action the user took once, twice.
  let busy = false;

  const nowIso = (): string => new Date(now()).toISOString();

  /** Say it where it can be HEARD and where it can be SEEN. A failure reported
   *  only to a visually-hidden region is a failure a sighted user never learns
   *  about (F-08).
   *
   *  Into its OWN line, not over the top of "that date was five days ago". The
   *  first version overwrote that, so pressing Set with an empty box cost the
   *  user the context this card exists to assemble — and did not give it back
   *  until the sheet was closed and reopened. */
  const say = (msg: string, alsoVisible = false): void => {
    SHEET_LIVE.textContent = msg;
    if (alsoVisible) { ERROR.textContent = msg; ERROR.hidden = false; }
  };

  /** Focus somewhere real after the sheet closes. Resolving a card REMOVES the
   *  row that opened it, and it may empty the whole section — so the guard has to
   *  have an else, or finishing the last one strands focus on <body>. */
  const restoreFocus = (): void => {
    if (!REGION.hidden) HEADING.focus();
    else document.querySelector<HTMLElement>('#capture')?.focus();
  };

  /**
   * EVERY passed date, in one act (3.9.0). See `resolveAllPassedEvents`.
   *
   * `busy` is the same one-write-at-a-time latch the per-card path uses, and it
   * matters more here: this is the surface where a double-tap would resolve
   * twenty things twice.
   *
   * It announces the COUNT it actually acted on, read before the commit, because
   * afterwards there is nothing left to count and "settled them" with no number
   * is the shape that asks somebody to take a bulk write on trust.
   */
  const resolveAll = async (choice: ReplanChoice): Promise<void> => {
    if (busy) return;
    busy = true;
    const n = passedDateCount(session.state(), nowIso(), session.zone);
    let landed = false;
    try {
      await session.commit(ctx =>
        resolveAllPassedEvents(ctx, session.state(), nowIso(), session.zone, choice));
      landed = true;
    } catch (err) {
      LIVE.textContent = `Couldn’t do that — ${(err as Error).message}`;
    } finally {
      busy = false;
    }
    if (!landed) return;
    try { onChange(); refresh(); } catch { /* a render bug must not contradict a landed write */ }
    const what = choice === 'undate'
      ? 'They keep their place and come back on their own.'
      : 'They are on the Menu, and nothing is owed.';
    const words = `${n === 1 ? 'One date' : `All ${n} dates`} settled. ${what} Nothing was marked done, and nothing was deleted.`;
    // THE LASTING REGION ONLY (ADR-0126). This act resolves EVERY passed date,
    // so the section is always empty afterwards and always hides — `#replan-live`
    // is gone by the time anybody could read it, which is what the a11y walk
    // reported as "matches nothing visible" in both themes. Writing both, which
    // is what this did, fixed the arrival and said it twice to a screen reader.
    sayLasting(words);
  };

  const resolve = async (choice: ReplanChoice, dayKey?: string): Promise<void> => {
    if (!current || busy) return;
    busy = true;
    const { node, passedKinds } = current;
    const label = node.title || '(untitled)';
    // Refusing rather than inventing. Asked of the SAME predicate the builder
    // uses, so the surface cannot believe a date is acceptable that the events
    // layer will then silently drop — which would look like a button that does
    // nothing at all.
    if (!canResolve(choice, dayKey)) { say('Pick a date first.', true); busy = false; return; }
    let landed = false;
    try {
      // EVERY passed clock, and the node's ACTUAL kind. Passing only the clock
      // the card names left the others live and the card came straight back;
      // letting the kind default to 'action' wrote a transition that never
      // happened into an append-only log (audit).
      await session.commit(ctx =>
        replanEvents(ctx, node.id, choice, passedKinds, dayKey, node.kind,
          demandClocksOf(session.state().nodes.get(node.id))));
      landed = true;
    } catch (err) {
      say(`Couldn’t do that — ${(err as Error).message}`, true);
    } finally {
      busy = false;
    }
    if (!landed) return;
    // From here the decision IS in the log, and nothing below may un-say it.
    try { onChange(); refresh(); } catch { /* a render bug must not contradict a landed write */ }
    // THE LASTING REGION ONLY, and this comment is the diagnosis ADR-0126 was
    // built from. It used to read: "Announce into #status as well as the
    // section's own region. Resolving the LAST card hides the whole section,
    // and a live region inside a hidden element announces nothing — so on the
    // one occasion most worth confirming, the confirmation would have been
    // silent." Every word of that is still true; writing BOTH was the half
    // that was wrong, because `#status` is `aria-live` too and a screen reader
    // heard it twice.
    //
    // `#status` unconditionally rather than only on the last card: whether this
    // section survives depends on how many passed dates are left, so the local
    // region is sometimes there and sometimes not. A sentence whose home varies
    // with the store is a sentence that reaches somebody only most of the time.
    const said = `${label}: ${OUTCOME[choice]}`;
    sayLasting(said);
    if (DLG.open) DLG.close();
    restoreFocus();
  };

  /** The five options, built once. `new-date` is the only one that needs an
   *  answer from the user, so it renders as a labeled date box beside its
   *  button rather than as a second hidden step. */
  function buildOptions(): void {
    OPTIONS.replaceChildren(...REPLAN_CHOICES.map(({ choice, label, hint }) => {
      const row = el('div', 'replan-option');
      if (choice === 'new-date') {
        const lab = el('label', 'replan-option-label', label);
        lab.htmlFor = 'replan-new-date';
        const input = el('input', 'replan-date');
        input.type = 'date';
        input.id = 'replan-new-date';
        // A new plan for a date already behind you is not a plan. `min` lets the
        // PLATFORM say so, which is better than the app refusing after the fact:
        // it is a date picker doing what date pickers do, not a judgment. If one
        // still gets through, the returning card says plainly that it went by —
        // which is true, so nothing is claimed that the data does not support.
        input.min = localDayKey(nowIso(), { zone: session.zone, boundary: boundaryOf(session.state()) });
        const set = el('button', 'replan-set', 'Set');
        set.type = 'button';
        set.addEventListener('click', () => void resolve('new-date', input.value));
        row.append(lab, input, set);
        row.append(el('span', 'replan-option-hint', hint));
        return row;
      }
      const b = el('button', 'replan-choice');
      b.type = 'button';
      b.append(el('span', 'replan-choice-label', label));
      // The consequence, spelled out. A control whose outcome is unclear is
      // expensive for this audience, and these outcomes are not guessable.
      b.append(el('span', 'replan-choice-hint', hint));
      b.addEventListener('click', () => void resolve(choice));
      row.append(b);
      return row;
    }));
  }

  function open(card: ReplanCard): void {
    current = card;
    TITLE.textContent = card.node.title || '(untitled)';
    WHEN.textContent = replanWords(card.daysAgo);
    const ctxWords = contextWords(card, session.zone);
    CONTEXT.textContent = ctxWords ?? '';
    CONTEXT.hidden = ctxWords === null;
    ERROR.textContent = '';
    ERROR.hidden = true;
    SHEET_LIVE.textContent = '';
    if (!DLG.open) DLG.showModal();
  }

  /**
   * Passed over this sitting (V2 stage 3, ADR-0079's rule applied here).
   *
   * IN MEMORY, AND NOWHERE ELSE. A skip that survived a reload would be a record
   * of a decision the app promised not to keep — and on THIS surface it would be
   * a durable list of the dates somebody could not face, which is worse than the
   * wall it replaces.
   */
  const passedOver = new Set<string>();

  function refresh(): void {
    const all = replanAll(session.state(), nowIso(), session.zone);
    const total = all.length;
    REGION.hidden = total === 0;
    if (total === 0) {
      LIST.replaceChildren();
      COUNT.textContent = '';
      return;
    }
    // Passing over DEMOTES a card; it never hides one. The cap is filled from
    // the un-passed cards first and then topped up from the passed ones, so the
    // surface always shows as many as it is allowed to show.
    //
    // Hiding was the first version and the walk caught what it cost: with two
    // dates and one passed over, the surface dropped to one card and the count
    // line changed from "2 dates have gone by." to "These 1 first." — a visible
    // record of what had just been avoided, on the surface least able to carry
    // one. Demotion has the same effect where it matters (with a backlog, the
    // next-worst comes forward) and no effect where it would be a tell.
    const fresh = all.filter(c => !passedOver.has(c.node.id));
    const cards = [...fresh, ...all.filter(c => passedOver.has(c.node.id))]
      .slice(0, REPLAN_CAP);
    // ALL OF THEM AT ONCE (3.9.0), and only when there is more than one — a
    // "settle all 1 of them" button beside a single card is a second way to press
    // the same thing, which is noise on the surface least able to carry any.
    //
    // The count it names is the TRUE TOTAL, not the three on screen: the cap
    // governs what a surface may show (law 8) and never what somebody has asked
    // for, which is the amnesty's own reasoning and the reason it applies no cap
    // of its own.
    if (BULK) {
      BULK.hidden = total < 2;
      if (total >= 2) {
        const line = el('p', 'replan-bulk-words', bulkWords(total));
        const row = el('div', 'replan-bulk-row');
        for (const { choice, label, hint } of BULK_CHOICES) {
          const b = el('button', 'replan-bulk-go');
          b.type = 'button';
          b.append(el('span', 'replan-bulk-label', label(total)));
          b.append(el('span', 'replan-bulk-hint', hint));
          b.addEventListener('click', () => { void resolveAll(choice); });
          row.append(b);
        }
        BULK.replaceChildren(line, row);
      } else {
        BULK.replaceChildren();
      }
    }
    // THE COUNT IS THE TRUE TOTAL, always. A number that shrank as things were
    // passed over would be the surface keeping score of what was avoided —
    // clarify's own rule, and the reason its skip records nothing either.
    COUNT.textContent = countWords(total, cards.length);
    LIST.replaceChildren(...cards.map(card => {
      const li = el('li', 'replan-card');
      const b = el('button', 'replan-open');
      b.type = 'button';
      b.append(el('span', 'replan-card-title', card.node.title || '(untitled)'));
      // States the fact in words, so nothing here depends on seeing a color
      // (B-01) — and it is a fact, never a rebuke.
      b.append(el('span', 'replan-card-when', replanWords(card.daysAgo)));
      b.addEventListener('click', () => open(card));
      li.append(b);
      // THE WAY PAST (ADR-0079, V2 stage 3). Without it this surface is the
      // triage wall in a second costume: `replanAll` is worst-first and stable,
      // so with a backlog of dates the same three cards sat at the top every
      // time the app opened, for ever, and the only way to be rid of one was to
      // make a decision about it. That is the wall this app exists to prevent.
      //
      // IN MEMORY AND NOWHERE ELSE, exactly as clarify's is. A durable list of
      // what somebody could not face is the wall rebuilt one layer down, with
      // the app keeping it for them.
      const skip = el('button', 'replan-skip linklike');
      skip.type = 'button';
      skip.textContent = 'Not this one';
      skip.setAttribute('aria-label', `Not this one — pass over ${card.node.title || '(untitled)'}, nothing is recorded`);
      skip.addEventListener('click', () => {
        passedOver.add(card.node.id);
        LIVE.textContent = 'Passed over. Nothing was recorded, and it is still here.';
        refresh();
      });
      li.append(skip);
      return li;
    }));
  }

  buildOptions();
  q<HTMLButtonElement>('#replan-close')?.addEventListener('click', () => {
    DLG.close();
    restoreFocus();
  });

  refresh();
  return { refresh };
}

export { REPLAN_CAP };
